import { auth } from "../firebase.config";

// What a portal page showed the last time, kept in this browser so the next
// visit can draw it at once (dimmed, nothing clickable) while this visit's
// data loads: stale-while-revalidate. Hooks/useLastVisit.js is the way pages
// use it; this file is only the storage.
//
// - Per person: the key carries the uid, and a copy is only read back for
//   the uid signed in now and for the same team (scope); the copies of anyone
//   else are dropped when someone signs in or out here, and sign-out clears
//   them (clearPageCache, and localStorage.clear() in App).
// - Versioned: PREFIX carries VERSION, and each page can pass its own version;
//   a deploy that changes what a page keeps bumps one of them and the old
//   copies are ignored (and swept).
// - Small: a page keeps at most `max` views or items (e.g. the last 10 orders
//   opened), one copy is at most MAX_ENTRY_CHARS and all of them together at
//   most MAX_TOTAL_CHARS (the oldest go first); not used after a week.
// - No payment details and no secrets: card data, payment methods, tokens and
//   signed file links are never written (sanitize), and pages leave out what
//   they don't need to draw.
// - Storage off or full (private windows, quota): nothing is kept and the
//   page loads as before.

export const VERSION = 1;
const ROOT = "pageCache:";
const PREFIX = `${ROOT}v${VERSION}:`;
// Earlier copies of the same idea, now folded into this one.
const LEGACY = ["listCache:", "everbary.refProgram:"];
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
export const MAX_ENTRY_CHARS = 150000;
export const MAX_TOTAL_CHARS = 1500000;

const storeKey = (page, uid) => `${PREFIX}${page}:${uid}`;

// Keys never kept, at any depth: payment details, secrets, chat history.
const SENSITIVE_KEY = /password|token|secret|cvc|cvv|iban|routing|fingerprint|clientSecret|^(paymentMethods?List|paymentMethods|PaypalData|paypalData|card|cards|last4|exp_?month|exp_?year|billingDetails|bankAccount|stripeCustomerId|customerId|setupIntent\w*|paymentIntent\w*|messages)$/i;
// Signed links (Storage download tokens, signed URLs) are left out as well.
const SIGNED_URL = /[?&](token|X-Goog-Signature|X-Amz-Signature|sig|signature)=/i;

export const sanitize = (value, depth = 0) => {
  if (depth > 12) return undefined;
  if (Array.isArray(value)) {
    return value.map((v) => sanitize(v, depth + 1)).filter((v) => v !== undefined);
  }
  if (value && typeof value === "object") {
    // Firestore Timestamps and the like: their milliseconds.
    if (typeof value.toMillis === "function") return value.toMillis();
    const out = {};
    Object.keys(value).forEach((key) => {
      if (SENSITIVE_KEY.test(key)) return;
      const v = sanitize(value[key], depth + 1);
      if (v !== undefined) out[key] = v;
    });
    return out;
  }
  if (typeof value === "string" && SIGNED_URL.test(value)) return undefined;
  if (typeof value === "function") return undefined;
  return value;
};

// A list's row as its table draws it: no files, notes, chat or history.
const ROW_LEFT_OUT = /files|notes|messages|reply|comments|history|password|token/i;
export const slimRow = (row) => {
  const out = {};
  Object.keys(row || {}).forEach((key) => {
    if (!ROW_LEFT_OUT.test(key)) out[key] = row[key];
  });
  return out;
};

const storage = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch (e) {
    return null;
  }
};

const readRaw = (key) => {
  try {
    const store = storage();
    return store ? JSON.parse(store.getItem(key)) : null;
  } catch (e) {
    return null;
  }
};

const allKeys = () => {
  const names = [];
  try {
    const store = storage();
    for (let i = 0; store && i < store.length; i += 1) names.push(store.key(i));
  } catch (e) {
    // Storage is off.
  }
  return names.filter(Boolean);
};

const remove = (key) => {
  try {
    storage().removeItem(key);
  } catch (e) {
    // Storage is off.
  }
};

// Is `uid` the person signed in on this tab now?
const signedInAs = (uid) => {
  try {
    return Boolean(uid) && Boolean(auth.currentUser) && auth.currentUser.uid === uid;
  } catch (e) {
    return false;
  }
};

// The copy of `page` kept for this person: the view/item `id` (or, with id
// null, the one saved last), for the same `scope` (team) and page `version`.
export const readPageCache = (page, uid, id, scope = "", version = 0) => {
  if (!page || !signedInAs(uid)) return null;
  const saved = readRaw(storeKey(page, uid));
  if (!saved || saved.uid !== uid || !saved.items || typeof saved.items !== "object") return null;
  const now = Date.now();
  const usable = (item) => item && item.scope === scope && (item.version || 0) === version
    && typeof item.at === "number" && now - item.at < MAX_AGE_MS && item.data !== undefined && item.data !== null;
  if (id !== null && id !== undefined) {
    const item = saved.items[id];
    return usable(item) ? item.data : null;
  }
  const newest = Object.values(saved.items).filter(usable).sort((a, b) => b.at - a.at)[0];
  return newest ? newest.data : null;
};

// Makes room for `need` more characters: the oldest copies of other pages go first.
const makeRoom = (keepKey, need) => {
  const pages = allKeys().filter((k) => k.startsWith(PREFIX) && k !== keepKey).map((key) => {
    let size = 0;
    let at = 0;
    try {
      const raw = storage().getItem(key) || "";
      size = raw.length;
      const parsed = JSON.parse(raw);
      at = Math.max(0, ...Object.values((parsed && parsed.items) || {}).map((i) => (i && i.at) || 0));
    } catch (e) {
      // Unreadable: it goes first.
    }
    return { key, size, at };
  });
  let total = pages.reduce((s, p) => s + p.size, 0);
  pages.sort((a, b) => a.at - b.at);
  while (pages.length && total + need > MAX_TOTAL_CHARS) {
    const oldest = pages.shift();
    remove(oldest.key);
    total -= oldest.size;
  }
};

// Keeps `data` as this person's copy of `page` (view/item `id`). At most
// `max` ids per page, the newest kept. Returns whether it was kept.
export const writePageCache = (page, uid, id, scope, data, { max = 4, version = 0 } = {}) => {
  if (!page || !signedInAs(uid) || id === null || id === undefined) return false;
  const store = storage();
  if (!store) return false;
  const key = storeKey(page, uid);
  const clean = sanitize(data);
  if (clean === undefined || clean === null) return false;
  const saved = readRaw(key);
  const items = saved && saved.uid === uid && saved.items && typeof saved.items === "object" ? { ...saved.items } : {};
  let one;
  try {
    one = JSON.stringify(clean);
  } catch (e) {
    return false;
  }
  if (one.length > MAX_ENTRY_CHARS) {
    // Too big to keep: no copy of this one (an older one would be wrong too).
    if (items[id]) {
      delete items[id];
      try {
        store.setItem(key, JSON.stringify({ uid, items }));
      } catch (e) {
        remove(key);
      }
    }
    return false;
  }
  items[id] = { at: Date.now(), scope: scope || "", version, data: clean };
  Object.keys(items)
    .sort((a, b) => (items[b].at || 0) - (items[a].at || 0))
    .slice(Math.max(1, max))
    .forEach((old) => delete items[old]);
  const value = JSON.stringify({ uid, items });
  makeRoom(key, value.length);
  try {
    store.setItem(key, value);
    return true;
  } catch (e) {
    // Full: drop every other copy and try once more; else keep nothing.
    allKeys().filter((k) => k.startsWith(ROOT) && k !== key).forEach(remove);
    try {
      store.setItem(key, value);
      return true;
    } catch (again) {
      remove(key);
      return false;
    }
  }
};

// Drops every copy except `uid`'s (all of them without one), the copies of
// other versions and the earlier caches this replaced.
export const clearPageCache = (uid = null) => {
  allKeys()
    .filter((name) => LEGACY.some((p) => name.startsWith(p))
      || (name.startsWith(ROOT) && (!name.startsWith(PREFIX) || !uid || !name.endsWith(`:${uid}`))))
    .forEach(remove);
};

// Someone signed in or out on this browser: only their own copies stay.
try {
  auth.onAuthStateChanged((user) => clearPageCache(user ? user.uid : null));
} catch (e) {
  // No Auth (tests): nothing to sweep.
}
